import { useState } from 'react';
import { Check, Copy, MailCheck } from 'lucide-react';

/**
 * Post-invite success panel: confirms the email was sent and offers the accept
 * link to copy (the "link + email" delivery decision). Shared by the admin Users
 * and reseller Staff invite modals.
 */
export function InviteLinkResult({ email, inviteUrl }: { email: string; inviteUrl: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — the link is still selectable in the field */
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-3">
        <MailCheck className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
        <div className="text-sm text-emerald-900 dark:text-emerald-200">
          Invitation emailed to <span className="font-medium">{email}</span>. It expires in 48 hours.
        </div>
      </div>
      <div>
        <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">
          Or share this link
        </div>
        <div className="flex items-stretch gap-2">
          <input
            readOnly
            value={inviteUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60 px-3 py-2 text-xs mono text-zinc-700 dark:text-zinc-200 truncate"
          />
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 px-3 rounded-md text-xs font-medium border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition shrink-0"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}
